import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import BugTrendChart from './BugTrendChart';
import {
  CheckCircleIcon,
  XMarkIcon,
  FireIcon,
  ExclamationTriangleIcon,
  BoltIcon,
  ChevronDoubleUpIcon,
  ChevronDownIcon,
  MinusIcon,
  CodeBracketIcon,
  MagnifyingGlassIcon,
  ArrowPathRoundedSquareIcon,
  PauseIcon,
  ArrowUturnLeftIcon,
  DocumentDuplicateIcon,
  XCircleIcon,
  ClockIcon,
  InformationCircleIcon,
  ArchiveBoxIcon,
  BugAntIcon,
  LightBulbIcon,
  ArrowTrendingUpIcon,
  RectangleStackIcon,
} from '@heroicons/react/24/outline';
import {
  Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, PieChart, Pie, Legend
} from 'recharts';

const CustomChartTooltipApp = ({ active, payload, total }) => {
  if (active && payload?.length) {
    const d = payload?.[0]?.payload;
    if (!d) return null;
    return (
      <div className="bg-white p-3 rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-50 text-center">
        <p className="font-bold text-gray-900 border-b border-gray-50 pb-1 mb-1 text-xs uppercase tracking-widest">{d.name}</p>
        <div className="flex items-center justify-between gap-4">
          <span className="text-[10px] font-medium text-gray-500">{d.value} items</span>
          <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
            {((d.value / (total || 1)) * 100).toFixed(1)}%
          </span>
        </div>
      </div>
    );
  }
  return null;
};

// ─── Shared Chart Styles ───────────────────────────────────────────────────────
const CHART_CONFIG = {
  innerRadius: 38,
  outerRadius: 52,
  margin: { top: 5, right: 10, bottom: 5, left: 10 },
  paddingAngle: 5,
  cx: "50%",
  cy: "50%"
};

const renderCustomLegend = (payload) => (
  <ul className="flex flex-wrap justify-center gap-x-4 gap-y-2 pt-2 px-2">
    {(payload || []).map((entry, index) => (
      <li key={index} className="flex items-center gap-1.5 text-[10px] font-bold text-gray-700 hover:opacity-80 transition-opacity">
        <span className="w-2 h-2 rounded-full shrink-0 shadow-sm" style={{ backgroundColor: entry.color }} />
        <span className="whitespace-nowrap uppercase tracking-wider">{entry.value}</span>
      </li>
    ))}
  </ul>
);

// ─── Work Distribution Donut Chart ────────────────────────────────────────────
const WorkDistributionChart = ({ analytics }) => {
  const data = [
    { name: 'Bugs', value: analytics?.bugTypeCount || 0, fill: '#EF4444' },
    { name: 'Observations', value: analytics?.observationTypeCount || 0, fill: '#F59E0B' },
    { name: 'Improvements', value: analytics?.improvementTypeCount || 0, fill: '#10B981' },
  ].filter(d => d.value > 0);

  const total = data.reduce((s, d) => s + d.value, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
      const d = payload?.[0]?.payload;
      if (!d) return null;
      return (
        <div className="bg-white p-3 rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-50">
          <p className="font-bold text-gray-900 border-b border-gray-50 pb-1 mb-1 text-xs uppercase tracking-widest">{d.name}</p>
          <div className="flex items-center justify-between gap-4">
            <span className="text-[10px] font-medium text-gray-500">{d.value} tickets</span>
            <span className="text-[10px] font-black text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
              {((d.value / (total || 1)) * 100).toFixed(1)}%
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 flex flex-col h-[320px] transition-all hover:shadow-md">
      <h3 className="text-xs font-black text-gray-800 uppercase tracking-[0.15em] mb-1">Work Distribution</h3>
      <p className="text-[10px] text-gray-400 font-medium mb-3">All ticket types</p>
      <div className="flex-1 min-h-0">
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={CHART_CONFIG.margin}>
              <Pie data={data} cx="50%" cy={CHART_CONFIG.cy} innerRadius={CHART_CONFIG.innerRadius} outerRadius={CHART_CONFIG.outerRadius}
                paddingAngle={CHART_CONFIG.paddingAngle} dataKey="value" stroke="none">
                {data.map((entry, i) => <Cell key={i} fill={entry.fill} className="hover:opacity-80 transition-opacity cursor-pointer" />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend verticalAlign="bottom" align="center" content={(props) => renderCustomLegend(props.payload)} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center space-y-2">
            <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center">
              <ArchiveBoxIcon className="h-6 w-6 text-gray-200" />
            </div>
            <p className="text-[10px] font-bold text-gray-300 uppercase tracking-widest">No tickets logged</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Bug Status Pie Chart Widget ───────────────────────────────────────────────
const BugStatusChartWidget = ({ sourceStats, selectedApp, isGlobal, navigate, userRole }) => {
  const [filter, setFilter] = useState('All Bugs');
  const [hiddenItems, setHiddenItems] = useState([]);

  const getBaseStats = () => {
    if (filter === 'My Assigned Bugs') return sourceStats?.assignedStats || {};
    if (isGlobal) {
      return {
        'Open': sourceStats.openBugs || 0,
        'In Progress': sourceStats.inProgressBugs || 0,
        'Retesting In Progress': sourceStats.retestedBugs || 0,
        'Reopen': sourceStats.reopenedBugs || 0,
        'Fixed': sourceStats.fixedBugs || 0,
        'Closed': sourceStats.closedBugs || 0,
        'Not a Bug': sourceStats.notABugCount || 0,
        'Rejected': sourceStats.rejectedBugs || 0,
        'Recurring Issue': sourceStats.recurringBugs || 0,
        'On Hold': sourceStats.onHoldBugs || 0
      };
    }
    return sourceStats || {};
  };

  const STATUS_CONFIG = {
    'Open': { ui: 'Open', type: 'active', color: '#6366F1' },
    'In Progress': { ui: 'In Progress', type: 'active', color: '#F59E0B' },
    'Retesting In Progress': { ui: 'Retesting', type: 'active', color: '#F97316' },
    'Reopen': { ui: 'Reopen', type: 'active', color: '#EF4444' },
    'Fixed': { ui: 'Fixed', type: 'resolved', color: '#10B981' },
    'Closed': { ui: 'Closed', type: 'resolved', color: '#064E3B' },
    'Not a Bug': { ui: 'Not a Bug', type: 'resolved', color: '#64748B' },
    'Rejected': { ui: 'Rejected', type: 'resolved', color: '#EF4444' },
    'Recurring Issue': { ui: 'Recurring', type: 'active', color: '#EF4444' },
    'On Hold': { ui: 'On Hold', type: 'active', color: '#F97316' }
  };

  const baseStats = getBaseStats();
  const rawData = [];
  for (const [key, val] of Object.entries(baseStats)) {
    if (typeof val !== 'number' || val === 0 || key === 'total') continue;
    const config = STATUS_CONFIG[key];
    if (!config) continue;
    if (filter === 'Active Bugs' && config.type !== 'active') continue;
    if (filter === 'Resolved Bugs' && config.type !== 'resolved') continue;
    rawData.push({ rawKey: key, name: config.ui, value: val, fill: config.color });
  }

  const chartData = rawData.map(d => ({
    ...d, value: hiddenItems.includes(d.name) ? 0 : d.value, displayValue: d.value
  }));
  const totalFiltered = chartData.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload?.length) {
      const d = payload?.[0]?.payload;
      if (!d || d.value === 0) return null;
      return (
        <div className="bg-white p-3 rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-gray-50">
          <p className="font-bold text-gray-900 border-b border-gray-50 pb-1 mb-1 text-xs uppercase tracking-widest">{d.name}</p>
          <div className="flex items-center justify-between gap-4">
            <span className="text-[10px] font-medium text-gray-500">{d.displayValue} bugs</span>
            <span className="text-[10px] font-black text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
              {((d.displayValue / (totalFiltered || 1)) * 100).toFixed(1)}%
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderLegend = (payload) => (
    <ul className="flex flex-wrap justify-center gap-x-4 gap-y-2 pt-3 px-3">
      {(payload || []).map((entry, index) => {
        const isHidden = hiddenItems.includes(entry.value);
        return (
          <li key={index}
            className={`flex items-center gap-1.5 cursor-pointer text-[10px] font-bold transition-all ${isHidden ? 'opacity-30 text-gray-300' : 'opacity-100 hover:opacity-80 text-gray-700'}`}
            onClick={() => setHiddenItems(p => p.includes(entry.value) ? p.filter(n => n !== entry.value) : [...p, entry.value])}>
            <span className="w-2 h-2 rounded-full shrink-0 shadow-sm" style={{ backgroundColor: entry.color }} />
            <span className="whitespace-nowrap uppercase tracking-wider">{entry.value}</span>
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 flex flex-col h-[320px] transition-all hover:shadow-md">
      <div className="flex flex-col gap-1 mb-3">
        <h3 className="text-xs font-black text-gray-800 uppercase tracking-[0.15em]">Bug Status Distribution</h3>
        <select
          className="w-full sm:w-[130px] text-[10px] border-gray-100 rounded-lg text-gray-600 py-1 pl-2 pr-6 focus:ring-indigo-500 bg-gray-50 cursor-pointer shadow-sm font-bold uppercase tracking-widest"
          value={filter} onChange={e => setFilter(e.target.value)}>
          <option value="All Bugs">All</option>
          <option value="Active Bugs">Active</option>
          <option value="Resolved Bugs">Resolved</option>
          {userRole !== 'developer' && userRole !== 'tester' && (
            <option value="My Assigned Bugs">Assigned</option>
          )}
        </select>
      </div>
      <div className="flex-1 min-h-0">
        {totalFiltered > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart margin={CHART_CONFIG.margin}>
              <Pie data={chartData} cx={CHART_CONFIG.cx} cy={CHART_CONFIG.cy} innerRadius={CHART_CONFIG.innerRadius} outerRadius={CHART_CONFIG.outerRadius}
                paddingAngle={CHART_CONFIG.paddingAngle} dataKey="value"
                onClick={data => navigate('/bugs', { state: { status: data.rawKey, selectedApplication: selectedApp || '' } })}
                style={{ cursor: 'pointer' }} stroke="none">
                {chartData.map((entry, i) => <Cell key={i} fill={entry.fill} className="hover:opacity-80 transition-opacity" />)}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend verticalAlign="bottom" align="center" content={(props) => renderLegend(props.payload)} />
            </PieChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center space-y-2">
            <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center">
              <BugAntIcon className="h-6 w-6 text-gray-200" />
            </div>
            <p className="text-[10px] font-bold text-gray-300 uppercase tracking-widest">No Bugs Found</p>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const AnalyticsDashboard = ({
  selectedApp,
  onAppChange,
  applications,
  onCreateAnnouncement,
  stats,
  getSeverityCount,
  getPriorityCount,
  severityColors,
  priorityColors,
  onRefreshApplications,
  onRefresh,
  quickActionsSidebar
}) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [applicationBugStats, setApplicationBugStats] = useState(() => {
    const cached = sessionStorage.getItem('dashboardAppStats');
    return cached ? JSON.parse(cached) : null;
  });
  const [appBugDistribution, setAppBugDistribution] = useState(() => {
    const cached = sessionStorage.getItem('dashboardAppDistribution');
    return cached ? JSON.parse(cached) : [];
  });
  const [isChartExpanded, setIsChartExpanded] = useState(false);

  const analytics = stats;
  const [activeTab, setActiveTab] = useState('All');

  // Source for ticket-type KPIs: either app-specific or global
  const typeStats = selectedApp ? applicationBugStats : analytics;

  const totalStats = React.useMemo(() => {
    const defaultStats = { active: 0, open: 0, inProgress: 0, retesting: 0, fixed: 0, onHold: 0, recurringIssue: 0, reopened: 0, duplicate: 0, rejected: 0, deferred: 0, notABug: 0, deprecated: 0, closed: 0, archived: 0, severity: {}, priority: {} };
    if (!typeStats) return defaultStats;
    const combined = { active: 0, open: 0, inProgress: 0, retesting: 0, fixed: 0, onHold: 0, recurringIssue: 0, reopened: 0, duplicate: 0, rejected: 0, deferred: 0, notABug: 0, deprecated: 0, closed: 0, archived: 0, severity: {}, priority: {} };
    ['bugStats', 'observationStats', 'improvementStats'].forEach(key => {
      const s = typeStats[key] || {};
      for (const k in combined) {
        if (k === 'severity' || k === 'priority') {
          if (s[k]) {
            for (const subK in s[k]) {
              combined[k][subK] = (combined[k][subK] || 0) + (s[k][subK] || 0);
            }
          }
        } else {
          combined[k] += (s[k] || 0);
        }
      }
    });
    return combined;
  }, [typeStats]);

  useEffect(() => {
    if (selectedApp) { fetchApplicationBugStats(selectedApp); }
    else {
      setApplicationBugStats(null);
      if (user?.role === 'developer' || user?.role === 'tester') fetchAppBugDistribution();
    }
    const intervalId = setInterval(() => {
      if (selectedApp) { fetchApplicationBugStats(selectedApp); }
      else if (user?.role === 'developer' || user?.role === 'tester') { fetchAppBugDistribution(); }
    }, 15000);
    return () => clearInterval(intervalId);
  }, [selectedApp, user]);

  useEffect(() => {
    const handleBugUpdated = () => {
      if (selectedApp) { fetchApplicationBugStats(selectedApp); }
      else if (user?.role === 'developer' || user?.role === 'tester') { fetchAppBugDistribution(); }
      if (onRefresh) onRefresh();
    };
    const handleStorage = e => { if (e.key === 'bugtracker:bug-updated') handleBugUpdated(); };
    window.addEventListener('bugtracker:bug-updated', handleBugUpdated);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('bugtracker:bug-updated', handleBugUpdated);
      window.removeEventListener('storage', handleStorage);
    };
  }, [selectedApp, user, onRefresh]);

  useEffect(() => {
    if (!selectedApp && (user?.role === 'developer' || user?.role === 'tester')) fetchAppBugDistribution();
  }, [applications, selectedApp, user]);

  const fetchApplicationBugStats = async (appId) => {
    try {
      const { data } = await axios.get(`/api/bugs/application-stats/${appId}`);
      setApplicationBugStats(data.data);
      sessionStorage.setItem('dashboardAppStats', JSON.stringify(data.data));
    } catch (err) {
      console.error('Failed to fetch application bug stats:', err);
      setApplicationBugStats(null);
    }
  };

  const fetchAppBugDistribution = async () => {
    try {
      const { data } = await axios.get('/api/bugs/app-distribution');
      setAppBugDistribution(data.data || []);
      sessionStorage.setItem('dashboardAppDistribution', JSON.stringify(data.data || []));
    } catch (err) {
      setAppBugDistribution([]);
    }
  };

  // (Unused) navToBugs removal

  return (
    <div className="space-y-5 sm:space-y-6">
      <div className={`grid grid-cols-1 ${selectedApp ? 'lg:grid-cols-5' : ''} gap-8 items-start`}>
        <div className={`${selectedApp ? 'lg:col-span-4 space-y-5 sm:space-y-6' : 'space-y-5 sm:space-y-6'}`}>
          {(analytics || selectedApp) && (
            <div className="space-y-5">

              {/* ── Total Work Items banner ────────────────────────────────────── */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Work Items – compact */}
                <div
                  onClick={() => setActiveTab('All')}
                  className={`cursor-pointer rounded-xl p-3 flex items-center gap-3 shadow-md hover:shadow-lg transition-all group ring-offset-2 ${activeTab === 'All'
                    ? 'bg-gradient-to-br from-indigo-700 to-indigo-800 ring-2 ring-indigo-500 scale-[1.02]'
                    : 'bg-gradient-to-br from-indigo-500 to-indigo-600 hover:scale-[1.01]'
                    }`}
                >
                  <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center shrink-0">
                    <RectangleStackIcon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 overflow-hidden">
                    <p className="text-indigo-100 text-[9px] font-bold uppercase tracking-widest truncate">Total Items</p>
                    <div className="flex items-baseline gap-1">
                      <p className="text-2xl font-bold text-white leading-none">
                        {typeStats?.totalWorkItems ?? (typeStats ? (typeStats.bugTypeCount || 0) + (typeStats.observationTypeCount || 0) + (typeStats.improvementTypeCount || 0) : 0)}
                      </p>
                    </div>
                    {activeTab === 'All' && <div className="mt-1" />}
                  </div>
                </div>

                {/* 3 type mini-cards */}
                {[
                  { id: 'Bug', label: 'Bugs', icon: BugAntIcon, iconColor: 'text-red-500', accentBg: 'bg-red-50', border: 'border-l-red-500', activeRing: 'ring-red-400', activeBg: 'bg-red-50/50', textColor: 'text-red-700', value: typeStats?.bugTypeCount || 0 },
                  { id: 'Observation', label: 'Observations', icon: LightBulbIcon, iconColor: 'text-amber-500', accentBg: 'bg-amber-50', border: 'border-l-amber-500', activeRing: 'ring-amber-400', activeBg: 'bg-amber-50/50', textColor: 'text-amber-700', value: typeStats?.observationTypeCount || 0 },
                  { id: 'Improvement', label: 'Improvements', icon: ArrowTrendingUpIcon, iconColor: 'text-emerald-500', accentBg: 'bg-emerald-50', border: 'border-l-emerald-500', activeRing: 'ring-emerald-400', activeBg: 'bg-emerald-50/50', textColor: 'text-emerald-700', value: typeStats?.improvementTypeCount || 0 },
                ].map((t, i) => (
                  <div key={i}
                    onClick={() => setActiveTab(t.id)}
                    className={`cursor-pointer bg-white rounded-xl shadow-sm border border-gray-100 border-l-4 ${t.border} p-3 flex items-center gap-3 transition-all group ring-offset-1 ${activeTab === t.id
                      ? `ring-2 ${t.activeRing} ${t.activeBg} shadow-md scale-[1.02]`
                      : 'hover:shadow-md hover:scale-[1.01]'
                      }`}>
                    <div className={`w-9 h-9 rounded-lg ${t.accentBg} flex items-center justify-center shrink-0`}>
                      <t.icon className={`h-5 w-5 ${t.iconColor} ${activeTab === t.id ? 'animate-pulse' : ''}`} />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider truncate">{t.label}</p>
                      <p className={`text-2xl font-bold ${t.textColor} leading-none`}>{t.value}</p>
                      {activeTab === t.id && <div className="mt-1" />}
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Ticket Type Detailed KPI sections ─────────────────────────── */}
              <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden mt-6">
                {/* Tab Content */}
                <div className="p-4 space-y-6">
                  {(() => {
                    const sec = {
                      'All': { statsKey: 'all', ticketType: null, accent: 'text-indigo-700', borderColor: 'border-indigo-400', deferred: { a: 'text-amber-700', b: 'border-l-amber-500' } },
                      'Bug': { statsKey: 'bugStats', ticketType: 'Bug', accent: 'text-red-700', borderColor: 'border-red-400', deferred: { a: 'text-amber-700', b: 'border-l-amber-500' } },
                      'Observation': { statsKey: 'observationStats', ticketType: 'Observation', accent: 'text-amber-700', borderColor: 'border-amber-400', deferred: { a: 'text-amber-700', b: 'border-l-amber-500' } },
                      'Improvement': { statsKey: 'improvementStats', ticketType: 'Improvement', accent: 'text-green-700', borderColor: 'border-green-400', deferred: { a: 'text-green-700', b: 'border-l-green-400' } },
                    }[activeTab];

                    const src = (activeTab === 'All' ? totalStats : typeStats?.[sec.statsKey]) || {};
                    const statusMap = {
                      'Open': 'Open',
                      'Dev In Progress': 'In Progress',
                      'QA In Progress': 'Retesting In Progress',
                      'Recurring Issue': 'Recurring Issue',
                      'On Hold': 'On Hold',
                      'Reopened': 'Reopen',
                      'Fixed': 'Fixed',
                      'Closed': 'Closed',
                      'Duplicate': 'Duplicate',
                      'Rejected': 'Rejected',
                      'Deferred': 'Deferred',
                      'Not a Bug': 'Not a Bug',
                      'Deprecated': 'Deprecated',
                    };

                    const nav = (label, isArchived = false, isTotal = false) => {
                      const p = new URLSearchParams();
                      if (isArchived) {
                        sessionStorage.setItem('bugListViewMode', 'archived');
                        p.set('view', 'archived');
                      }
                      else if (!isTotal) {
                        const status = statusMap[label];
                        if (status) p.set('status', status);
                      }
                      if (sec.ticketType) p.set('ticketType', sec.ticketType);
                      if (selectedApp) p.set('application', selectedApp);
                      p.set('page', '1');
                      navigate(`/bugs?${p.toString()}`);
                    };

                    return (
                      <div className="space-y-6">
                        {/* Tier 2: Status Breakdown */}
                        <div className="space-y-4">
                          <div className="flex items-center gap-3 px-1">
                            <div className="px-2.5 py-1 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-2">
                              <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                              <h3 className="text-[10px] font-extrabold text-gray-500 uppercase tracking-[0.15em] leading-none">Activity Breakdown</h3>
                            </div>
                            <div className="h-px bg-gray-100 flex-1" />
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                            {[
                              { label: 'Open', value: src.open, accent: 'bg-blue-500', text: 'text-blue-600', icon: ExclamationTriangleIcon, iconColor: 'text-blue-500' },
                              { label: 'Dev In Progress', value: src.inProgress, accent: 'bg-orange-500', text: 'text-orange-600', icon: CodeBracketIcon, iconColor: 'text-orange-500' },
                              { label: 'QA In Progress', value: src.retesting, accent: 'bg-indigo-500', text: 'text-indigo-600', icon: MagnifyingGlassIcon, iconColor: 'text-indigo-500' },
                              { label: 'Fixed', value: src.fixed, accent: 'bg-emerald-500', text: 'text-emerald-600', icon: CheckCircleIcon, iconColor: 'text-emerald-500' },
                              { label: 'Closed', value: src.closed, accent: 'bg-green-600', text: 'text-green-700', icon: CheckCircleIcon, iconColor: 'text-green-600' },
                              { label: 'On Hold', value: src.onHold, accent: 'bg-amber-500', text: 'text-amber-600', icon: PauseIcon, iconColor: 'text-amber-500' },
                              { label: 'Recurring Issue', value: src.recurringIssue, accent: 'bg-red-500', text: 'text-red-600', icon: ArrowPathRoundedSquareIcon, iconColor: 'text-red-500' },
                              { label: 'Reopened', value: src.reopened, accent: 'bg-rose-500', text: 'text-rose-600', icon: ArrowUturnLeftIcon, iconColor: 'text-rose-500' },
                              { label: 'Duplicate', value: src.duplicate, accent: 'bg-purple-500', text: 'text-purple-600', icon: DocumentDuplicateIcon, iconColor: 'text-purple-500' },
                              { label: 'Rejected', value: src.rejected, accent: 'bg-slate-500', text: 'text-slate-600', icon: XCircleIcon, iconColor: 'text-slate-500' },
                              { label: 'Deferred', value: src.deferred, accent: 'bg-amber-400', text: 'text-amber-600', icon: ClockIcon, iconColor: 'text-amber-400' },
                              { label: 'Not a Bug', value: src.notABug, accent: 'bg-sky-500', text: 'text-sky-600', icon: InformationCircleIcon, iconColor: 'text-sky-500' },
                              { label: 'Deprecated', value: src.deprecated, accent: 'bg-gray-400', text: 'text-gray-500', icon: ArchiveBoxIcon, iconColor: 'text-gray-400' },
                              { label: 'Archived', value: src.archived, accent: 'bg-zinc-600', text: 'text-zinc-600', icon: ArchiveBoxIcon, iconColor: 'text-zinc-600', isArchive: true },
                            ].map(st => (
                              <button key={st.label} onClick={() => nav(st.label, st.isArchive)}
                                className="text-left bg-white border border-gray-100 border-t-2 rounded-xl p-3 shadow-sm hover:shadow-md transition-all group active:scale-[0.98] flex flex-col justify-between min-h-[90px]"
                                style={{ borderTopColor: st.accent.replace('bg-', '#').replace('500', '500').replace('600', '600').replace('orange', 'f97316').replace('blue', '3b82f6').replace('emerald', '10b981').replace('green', '16a34a').replace('indigo', '6366f1').replace('amber', 'f59e0b').replace('red', 'ef4444').replace('rose', 'f43f5e').replace('purple', 'a855f7').replace('slate', '64748b').replace('sky', '0ea5e9').replace('zinc', '52525b').replace('gray', '9ca3af') }}>
                                <div className="flex items-start justify-between mb-2">
                                  <div className="flex items-center gap-1.5 overflow-hidden">
                                    <span className={`w-1.25 h-1.25 rounded-full ${st.accent} shrink-0`} />
                                    <p className="text-[9px] font-bold text-gray-500 uppercase tracking-wider truncate leading-tight">{st.label}</p>
                                  </div>
                                  <st.icon className={`h-3.5 w-3.5 ${st.iconColor} opacity-40 group-hover:opacity-100 transition-opacity shrink-0`} />
                                </div>
                                <p className={`text-3xl font-bold ${st.text} leading-none antialiased`}>{st.value || 0}</p>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Tier 3: Severity & Priority Breakdown */}
                        <div className="relative pt-6 border-t border-gray-100">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 relative">
                            {/* Middle Vertical Divider */}
                            <div className="hidden md:block absolute left-1/2 top-4 bottom-4 w-px bg-gray-100 -translate-x-1/2" />

                            {/* Severity */}
                            <div className="space-y-4">
                              <div className="flex items-center gap-3 px-1">
                                <div className="px-2.5 py-1 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-2">
                                  <h3 className="text-[10px] font-extrabold text-gray-500 uppercase tracking-[0.15em] leading-none">By Severity</h3>
                                </div>
                                <div className="h-px bg-gray-100 flex-1" />
                              </div>
                              <div className="space-y-1">
                                {[
                                  { name: 'Critical', color: 'text-red-600', dot: 'bg-red-500', icon: FireIcon },
                                  { name: 'High', color: 'text-orange-600', dot: 'bg-orange-500', icon: ExclamationTriangleIcon },
                                  { name: 'Medium', color: 'text-amber-600', dot: 'bg-amber-500', icon: BoltIcon },
                                  { name: 'Low', color: 'text-emerald-600', dot: 'bg-emerald-500', icon: CheckCircleIcon },
                                ].map(sev => {
                                  const count = (src.severity?.[sev.name]) || 0;
                                  return (
                                    <button
                                      key={sev.name}
                                      onClick={() => {
                                        const p = new URLSearchParams();
                                        p.set('severity', sev.name);
                                        if (sec.ticketType) p.set('ticketType', sec.ticketType);
                                        if (selectedApp) p.set('application', selectedApp);
                                        navigate(`/bugs?${p.toString()}`);
                                      }}
                                      className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-all group group-hover:scale-[1.01]"
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className={`w-1.5 h-1.5 rounded-full ${sev.dot}`} />
                                        <div className="flex items-center gap-2 text-gray-500 group-hover:text-gray-900 transition-colors">
                                          <sev.icon className="h-4 w-4 opacity-40 group-hover:opacity-100" />
                                          <span className="text-[10px] font-extrabold uppercase tracking-widest">{sev.name}</span>
                                        </div>
                                      </div>
                                      <span className={`text-base font-bold ${sev.color}`}>{count}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Priority */}
                            <div className="space-y-4">
                              <div className="flex items-center gap-3 px-1">
                                <div className="px-2.5 py-1 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-2">
                                  <h3 className="text-[10px] font-extrabold text-gray-500 uppercase tracking-[0.15em] leading-none">By Priority</h3>
                                </div>
                                <div className="h-px bg-gray-100 flex-1" />
                              </div>
                              <div className="space-y-1">
                                {[
                                  { name: 'High', color: 'text-rose-600', dot: 'bg-rose-500', icon: ChevronDoubleUpIcon },
                                  { name: 'Medium', color: 'text-amber-600', dot: 'bg-amber-500', icon: MinusIcon },
                                  { name: 'Low', color: 'text-sky-600', dot: 'bg-sky-500', icon: ChevronDownIcon },
                                ].map(prio => {
                                  const count = (src.priority?.[prio.name]) || 0;
                                  return (
                                    <button
                                      key={prio.name}
                                      onClick={() => {
                                        const p = new URLSearchParams();
                                        p.set('priority', prio.name);
                                        if (sec.ticketType) p.set('ticketType', sec.ticketType);
                                        if (selectedApp) p.set('application', selectedApp);
                                        navigate(`/bugs?${p.toString()}`);
                                      }}
                                      className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-gray-50 transition-all group group-hover:scale-[1.01]"
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className={`w-1.5 h-1.5 rounded-full ${prio.dot}`} />
                                        <div className="flex items-center gap-2 text-gray-500 group-hover:text-gray-900 transition-colors">
                                          <prio.icon className="h-4 w-4 opacity-40 group-hover:opacity-100" />
                                          <span className="text-[10px] font-extrabold uppercase tracking-widest">{prio.name}</span>
                                        </div>
                                      </div>
                                      <span className={`text-base font-bold ${prio.color}`}>{count}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {selectedApp && applicationBugStats && (
            <div className="bg-white shadow-sm rounded-xl border border-gray-100 p-4 sm:p-5">
              <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-3">30-Day Bug Activity</h2>
              <BugTrendChart selectedApp={selectedApp} compact={false} />
            </div>
          )}
        </div>

        {selectedApp && (
          <div className="lg:col-span-1 space-y-4">
            {quickActionsSidebar}
            {applicationBugStats && (
              <div className="space-y-4">
                <WorkDistributionChart analytics={applicationBugStats} />
                <BugStatusChartWidget sourceStats={applicationBugStats} selectedApp={selectedApp} isGlobal={false} navigate={navigate} userRole={user?.role} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Charts section ──────────────────────────────────────────────────── */}
      {!selectedApp && analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
          <div className="lg:col-span-1">
            <WorkDistributionChart analytics={analytics} />
          </div>
          <div className="lg:col-span-1">
            <BugStatusChartWidget sourceStats={analytics} selectedApp={selectedApp} isGlobal={true}
              navigate={navigate} userRole={user?.role} />
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-5 flex flex-col h-[320px] transition-all hover:shadow-md">
            <h3 className="text-xs font-black text-gray-800 uppercase tracking-[0.15em] mb-1">App Distribution</h3>
            <p className="text-[10px] text-gray-400 font-medium mb-3">Top applications by volume</p>
            <div className="flex-1 min-h-0">
              {appBugDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart margin={CHART_CONFIG.margin}>
                    <Pie
                      data={(() => {
                        const sorted = [...appBugDistribution].sort((a, b) => b.bugCount - a.bugCount);
                        const top = sorted.slice(0, 5);
                        const othersCount = sorted.slice(5).reduce((a, c) => a + c.bugCount, 0);
                        const d = top.map(app => ({ name: app.appName, value: app.bugCount }));
                        if (othersCount > 0) d.push({ name: 'Others', value: othersCount });
                        return d;
                      })()}
                      cx="50%" cy={CHART_CONFIG.cy} innerRadius={CHART_CONFIG.innerRadius} outerRadius={CHART_CONFIG.outerRadius} paddingAngle={CHART_CONFIG.paddingAngle} dataKey="value" stroke="none">
                      {['#6366F1', '#A855F7', '#EC4899', '#F43F5E', '#F97316', '#94A3B8'].map((c, i) => <Cell key={i} fill={c} className="hover:opacity-80 transition-opacity cursor-pointer" />)}
                    </Pie>
                    <Tooltip content={<CustomChartTooltipApp total={appBugDistribution.reduce((s, i) => s + i.bugCount, 0)} />} />
                    <Legend verticalAlign="bottom" align="center" content={(props) => renderCustomLegend(props.payload)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center space-y-2">
                  <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center">
                    <ArchiveBoxIcon className="h-6 w-6 text-gray-200" />
                  </div>
                  <p className="text-[10px] font-bold text-gray-300 uppercase tracking-widest">No App Data</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}



      {/* ── Expanded chart modal ───────────────────────────────────────────── */}
      {isChartExpanded && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={() => setIsChartExpanded(false)}>
          <div className="bg-white rounded-lg shadow-2xl w-full h-full max-w-6xl max-h-screen overflow-auto"
            onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-6 flex items-center justify-between z-10">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Application-Wise Ticket Distribution</h2>
              <button onClick={() => setIsChartExpanded(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <XMarkIcon className="h-6 w-6 text-gray-600" />
              </button>
            </div>
            <div className="p-4 sm:p-6 overflow-x-auto">
              <div className="h-96 sm:h-screen" style={{ minWidth: `${Math.max(appBugDistribution.length * 100, 1200)}px` }}>
                {appBugDistribution.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={appBugDistribution} margin={{ top: 20, right: 30, left: 0, bottom: 100 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="appName" angle={-45} textAnchor="end" height={150} tick={{ fontSize: 14 }} />
                      <YAxis label={{ value: 'Ticket Count', angle: -90, position: 'insideLeft' }} tick={{ fontSize: 14 }} />
                      <Tooltip formatter={v => [v, 'Tickets']} labelFormatter={l => `App: ${l}`} />
                      <Bar dataKey="bugCount" fill="#8B5CF6" radius={[8, 8, 0, 0]}>
                        {appBugDistribution.map((_, i) => {
                          const colors = ['#8B5CF6', '#F59E0B', '#EF4444', '#F97316', '#EC4899', '#A855F7', '#FB923C', '#DC2626'];
                          return <Cell key={i} fill={colors[i % colors.length]} />;
                        })}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-center text-gray-500"><p>No application data available</p></div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDashboard;
